import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en/clinician.json';
import fr from './locales/fr/clinician.json';

const SUPPORTED = ['en', 'fr'] as const;
const STORAGE_KEY = 'i18nextLng';
const COOKIE_KEY = 'i18next';

const isSupported = (lang: string | null | undefined): lang is (typeof SUPPORTED)[number] =>
  !!lang && (SUPPORTED as readonly string[]).includes(lang);

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

const writeCookie = (name: string, value: string) => {
  if (typeof document === 'undefined') return;
  const days = 60 * 60 * 24 * 30;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${days}; samesite=lax`;
};

/**
 * Mirrors the v1 detection order — querystring (`?lang=`) → cookie → localStorage → fallback.
 * Picking up the v1 storage keys (`i18next` cookie, `i18nextLng` localStorage) means a session
 * carried over from the v1 app keeps the user's existing language preference.
 */
const detectInitialLanguage = (): (typeof SUPPORTED)[number] => {
  if (typeof window === 'undefined') return 'en';
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('lang');
  if (isSupported(fromUrl)) return fromUrl;

  const fromCookie = readCookie(COOKIE_KEY);
  if (isSupported(fromCookie)) return fromCookie;

  try {
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (isSupported(fromStorage)) return fromStorage;
  } catch {
    // storage may be blocked; fall through
  }
  return 'en';
};

let initialized = false;

export function initI18n() {
  if (initialized) return i18n;
  i18n.use(initReactI18next).init({
    // V1 loaded the same JSON under the `translation` namespace (the i18next default).
    // Keeping that exact shape so existing translation lookups behave identically.
    fallbackLng: 'en',
    lng: detectInitialLanguage(),
    supportedLngs: [...SUPPORTED],
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    keySeparator: false,
    nsSeparator: '|',
  });

  // Persist whatever the app switches to so the next visit comes up the same.
  i18n.on('languageChanged', (lng) => {
    if (!isSupported(lng)) return;
    try {
      localStorage.setItem(STORAGE_KEY, lng);
    } catch {
      // ignore quota / private mode
    }
    writeCookie(COOKIE_KEY, lng);
  });

  initialized = true;
  return i18n;
}
